import type { BillingServiceKey } from './eventSupportApi';

export type EventCompletionStatus = 'NOT_COMPLETED' | 'COMPLETED';

export type EventCompletionServicePreview = {
  billing_service_key: BillingServiceKey;
  name: string;
  requested_count: number;
  done_count: number;
  recorded_result_count: number;
  can_remove: boolean;
  warnings: string[];
};

export type EventCompletionPreviewResponse = {
  event_id: number;
  event_completion_status: EventCompletionStatus;
  service_requests_version: string | null;
  services: EventCompletionServicePreview[];
  warnings: string[];
};

export type EventCompletionResponse = {
  event_id: number;
  event_completion_status: 'COMPLETED';
  event_completed_at: string;
  event_completed_by_user_id: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

const isBillingServiceKey = (value: unknown): value is BillingServiceKey =>
  value === 'RESPIRATOR_MEDICAL' ||
  value === 'FIT_TEST' ||
  value === 'PULMONARY_FUNCTION_TEST' ||
  value === 'AUDIOMETRY';

const isCompletionServicePreview = (value: unknown): value is EventCompletionServicePreview => {
  if (!isRecord(value)) return false;
  return (
    isBillingServiceKey(value.billing_service_key) &&
    typeof value.name === 'string' &&
    isNonNegativeInteger(value.requested_count) &&
    isNonNegativeInteger(value.done_count) &&
    isNonNegativeInteger(value.recorded_result_count) &&
    typeof value.can_remove === 'boolean' &&
    Array.isArray(value.warnings) &&
    value.warnings.every((warning) => typeof warning === 'string')
  );
};

export const parseEventCompletionPreview = (value: unknown): EventCompletionPreviewResponse | null => {
  if (!isRecord(value)) return null;
  if (
    typeof value.event_id !== 'number' ||
    (value.event_completion_status !== 'NOT_COMPLETED' && value.event_completion_status !== 'COMPLETED') ||
    (value.service_requests_version !== null && typeof value.service_requests_version !== 'string') ||
    !Array.isArray(value.services) ||
    !value.services.every(isCompletionServicePreview) ||
    !Array.isArray(value.warnings) ||
    !value.warnings.every((warning) => typeof warning === 'string')
  ) {
    return null;
  }
  return value as EventCompletionPreviewResponse;
};

export const parseEventCompletionResponse = (value: unknown): EventCompletionResponse | null => {
  if (!isRecord(value)) return null;
  if (
    typeof value.event_id !== 'number' ||
    value.event_completion_status !== 'COMPLETED' ||
    typeof value.event_completed_at !== 'string' ||
    typeof value.event_completed_by_user_id !== 'string'
  ) {
    return null;
  }
  return value as EventCompletionResponse;
};

export const eventCompletionErrorMessage = (body: unknown, statusCode?: number): string => {
  if (isRecord(body) && typeof body.detail === 'string' && body.detail.trim()) {
    return body.detail.trim();
  }
  if (statusCode === 403) return 'You do not have permission to complete this event.';
  if (statusCode === 404) return 'The event could not be found.';
  if (statusCode === 409) return 'The event changed while you were reviewing it. Reload the preview.';
  if (statusCode === 422) return 'The service details must be corrected before completing this event.';
  return 'The event could not be completed. Please try again.';
};
